"use client";

import type { StepId } from "@/features/multistep/registry/types";
import { useFormContext, useWatch } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { TIPOS_SOLICITUD, TIPOS_TERCERO } from "@/config/form-options";
import { useEffect } from "react";

export function GeneralidadesStep({ stepId }: { stepId: StepId }) {
  const { control, trigger, setValue, clearErrors } = useFormContext();
  const pais = useWatch({ control, name: `${stepId}.pais` });
  const departamento = useWatch({ control, name: `${stepId}.departamento` });

  useEffect(() => {
    void trigger(`${stepId}`);
  }, [pais]);

  useEffect(() => {
    // Limpia el departamento cuando el país cambia y no es Colombia.
    if (pais?.toLowerCase() !== "colombia") {
      setValue(`${stepId}.departamento`, undefined, { shouldValidate: false });
      clearErrors(`${stepId}.departamento`);
    } else {
      // Si el país es Colombia, asegura que el campo departamento exista para la validación.
      // Puedes limpiar el valor para que el usuario seleccione uno nuevo.
      if (departamento) {
        setValue(`${stepId}.departamento`, undefined);
      }
    }
    // Llama a trigger para revalidar el campo 'departamento' inmediatamente.
    // Aunque el refine lo hará automáticamente, esto asegura que el UI refleje el cambio rápidamente.
    void trigger(`${stepId}.departamento`);
  }, [pais, stepId, trigger, setValue, clearErrors]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-x-6">
      {/* Tipo de solicitud */}
      <FormField
        control={control}
        name={`${stepId}.tipoSolicitud`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tipo de solicitud</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona el tipo de solicitud" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_SOLICITUD.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Tipo de tercero */}
      <FormField
        control={control}
        name={`${stepId}.tipoTercero`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tipo de tercero</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona el tipo de tercero" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_TERCERO.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
